import { useEffect, useState } from 'react'

import FileService from '@/modules/file/fileService'

export interface ReceiptFileReference {
  fileId?: string | number
  id?: string | number
}
type AssistantDocument = Record<string, unknown>

const receiptFileId = (receipt?: ReceiptFileReference | null) => receipt?.fileId ?? receipt?.id

/**
 * Hydrates the full DMS file behind the receipt currently visible in the quick-detail carousel.
 *
 * Expense/revenue payloads intentionally carry only small receipt references. Max and the report
 * action need the full file (minioId, analysis, review data), so this read-only lookup stays local
 * to the optional assistant band. The keyed state prevents a late response for slide A from being
 * rendered after the user has already switched to slide B.
 */
export const useReceiptDetailAssistantDocument = (
  enabled: boolean,
  receipt?: ReceiptFileReference | null,
): AssistantDocument | null => {
  const fileId = receiptFileId(receipt)
  const lookupKey = enabled && fileId !== undefined && fileId !== null ? `${fileId}` : null
  const [lookup, setLookup] = useState<{
    key: string | null
    document: AssistantDocument | null
  }>({ key: null, document: null })

  useEffect(() => {
    if (!lookupKey) return undefined

    let cancelled = false
    FileService.fetchFileInfo(fileId)
      .then(({ body }) => {
        if (!cancelled) setLookup({ key: lookupKey, document: body ?? null })
      })
      .catch(() => {
        if (!cancelled) setLookup({ key: lookupKey, document: null })
      })

    return () => { cancelled = true }
  }, [fileId, lookupKey])

  return lookup.key === lookupKey ? lookup.document : null
}
